import React, { useState, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Stack,
  CircularProgress,
  Divider,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  FormControlLabel,
  Checkbox,
  Switch,
  Autocomplete,
  Tooltip,
  IconButton,
} from '@mui/material'
import { CheckCircle, InfoOutlined } from '@mui/icons-material'
import { apiClient } from '@/core/api/apiClient'
import { fileToBase64 } from '@/core/utils/fileUtils'
import { TransporterRole, TransporterWithPackages } from '@otw/models'
import { useAdminTransporter } from '@/hooks/admin/useAdminTransporter'
import { useRepairShopLocations, filterRepairShopsForPicker } from '@/hooks/inventory/useRepairShopLocations'

/** Derived for parent success handlers (legacy repair/transporter/both labeling). */
type LogisticsAccessLabel = 'transporter' | 'repair' | 'both'

function SectionHeading({
  title,
  info,
  dense,
}: {
  title: string
  info: React.ReactNode
  /** Tighter spacing for nested sections (e.g. driver license card). */
  dense?: boolean
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        mb: dense ? 1 : 2,
      }}
    >
      <Typography variant="subtitle2" fontWeight={600}>
        {title}
      </Typography>
      <Tooltip title={info} placement="left" arrow enterTouchDelay={0}>
        <IconButton size="small" aria-label={`About ${title}`} sx={{ color: 'text.secondary' }}>
          <InfoOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

interface CreateUserDialogProps {
  open: boolean
  onClose: () => void
  onSuccess: (userData: {
    id: string
    email: string
    firstName: string
    lastName: string
    phoneNumber: string
    transporterRole: TransporterRole | 'repair'
    userType?: LogisticsAccessLabel
    elevated?: boolean
    driverLicenseFrontStoragePath?: string
    companyId?: string
    companyName?: string
    repairShopId?: string
    repairShopIds?: string[]
    repairShopName?: string
  }) => void
  userToEdit?: TransporterWithPackages | null
  repairUsersById?: Map<string, { shop_ids: string[]; shop_id?: string; name: string }>
  transporterById?: Map<
    string,
    {
      transporter_role?: TransporterRole
      elevated?: boolean
      driver_license_front_storage_path?: string
    }
  >
}

const CreateUserDialog: React.FC<CreateUserDialogProps> = ({
  open,
  onClose,
  onSuccess,
  userToEdit,
  repairUsersById,
  transporterById,
}) => {
  const isEditMode = !!userToEdit
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [transporterRole, setTransporterRole] = useState<TransporterRole>('owner')
  const [grantRepairAccess, setGrantRepairAccess] = useState(false)
  const [grantTransporterAccess, setGrantTransporterAccess] = useState(true)
  const [elevated, setElevated] = useState(false)
  const [selectedCompanyId, setSelectedCompanyId] = useState('')
  const [selectedRepairShopIds, setSelectedRepairShopIds] = useState<string[]>([])
  const [includeInactiveRepairShops, setIncludeInactiveRepairShops] = useState(false)
  const [driverLicenseFrontStoragePath, setDriverLicenseFrontStoragePath] = useState('')
  const [driverLicenseFrontUrl, setDriverLicenseFrontUrl] = useState('')
  const [isUploadingLicensePhoto, setIsUploadingLicensePhoto] = useState(false)
  const [licensePhotoError, setLicensePhotoError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    transporterRole: '',
    access: '',
    repairShop: '',
  })

  // Get transporter groups for company selection
  const { transporterGroups, updateTransporterGroup } = useAdminTransporter()
  const { repairShopLocations } = useRepairShopLocations()

  // Populate form when editing
  useEffect(() => {
    if (isEditMode && userToEdit) {
      const userId = userToEdit.user?.id || userToEdit.user_id || userToEdit.id
      const hasRepairAccess = userId ? repairUsersById?.has(userId) ?? false : false
      const linkedRepairUser = userId ? repairUsersById?.get(userId) : undefined
      const linkedTransporter = userId ? transporterById?.get(userId) : undefined
      const isRepairUser = (userToEdit.transporter_role as string) === 'repair'
      const hasTransporterAccess = Boolean(linkedTransporter) || !isRepairUser
      setEmail(userToEdit.email || '')
      setFirstName(userToEdit.first_name || '')
      setLastName(userToEdit.last_name || '')
      setPhoneNumber(userToEdit.phone || '')
      const effectiveTransporterRole = linkedTransporter?.transporter_role || (
        isRepairUser ? 'owner' : (userToEdit.transporter_role || 'owner')
      )
      setTransporterRole(effectiveTransporterRole as TransporterRole)
      setGrantRepairAccess(hasRepairAccess)
      setGrantTransporterAccess(hasTransporterAccess)
      setElevated(linkedTransporter?.elevated || userToEdit.elevated || false)
      setSelectedRepairShopIds(
        linkedRepairUser?.shop_ids?.length
          ? [...linkedRepairUser.shop_ids]
          : linkedRepairUser?.shop_id
            ? [linkedRepairUser.shop_id]
            : (userToEdit as any).shop_id
              ? [(userToEdit as any).shop_id]
              : []
      )
      setDriverLicenseFrontStoragePath(
        linkedTransporter?.driver_license_front_storage_path || userToEdit.driver_license_front_storage_path || ''
      )
      setDriverLicenseFrontUrl('')
      setLicensePhotoError('')
    } else {
      // Reset form when creating new user
      setEmail('')
      setFirstName('')
      setLastName('')
      setPhoneNumber('')
      setTransporterRole('owner')
      setGrantRepairAccess(false)
      setGrantTransporterAccess(true)
      setElevated(false)
      setSelectedCompanyId('')
      setSelectedRepairShopIds([])
      setDriverLicenseFrontStoragePath('')
      setDriverLicenseFrontUrl('')
      setLicensePhotoError('')
    }
  }, [isEditMode, userToEdit, open, repairUsersById, transporterById])

  useEffect(() => {
    const loadLicensePhotoUrl = async () => {
      if (!open || !driverLicenseFrontStoragePath) return
      try {
        const result = await apiClient.portal.admin.getTransporterLicenseFrontUrl(driverLicenseFrontStoragePath)
        const signedUrl = (result as any)?.data?.url ?? (result as any)?.url
        if (signedUrl) {
          setDriverLicenseFrontUrl(signedUrl)
        }
      } catch (error) {
        console.error('Failed loading transporter license photo URL:', error)
      }
    }
    loadLicensePhotoUrl()
  }, [open, driverLicenseFrontStoragePath])

  const validateForm = () => {
    const newErrors = {
      email: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      transporterRole: '',
      access: '',
      repairShop: '',
    }

    if (!grantRepairAccess && !grantTransporterAccess) {
      newErrors.access = 'Select at least one: repair dashboard access and/or transport (photo uploader) access.'
    }
    
    if (!email) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Invalid email format'
    }
    
    if (!firstName.trim()) newErrors.firstName = 'First name is required'
    if (!lastName.trim()) newErrors.lastName = 'Last name is required'
    
    const phoneDigits = phoneNumber.replace(/\D/g, '')
    if (!phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required'
    } else if (phoneDigits.length !== 10) {
      newErrors.phoneNumber = 'Phone number must be 10 digits'
    }

    if (grantTransporterAccess && !transporterRole) {
      newErrors.transporterRole = 'Transporter role is required'
    }

    if (grantRepairAccess && selectedRepairShopIds.length === 0) {
      newErrors.repairShop = 'Select at least one repair shop'
    }

    setErrors(newErrors)
    return !Object.values(newErrors).some(error => error)
  }

  const resolveAccessLabel = (
    grantRepair: boolean,
    grantTransport: boolean
  ): LogisticsAccessLabel => {
    if (grantRepair && grantTransport) return 'both'
    if (grantRepair) return 'repair'
    return 'transporter'
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    try {
      setIsSubmitting(true)
      
      if (isEditMode && userToEdit) {
        // Edit existing user
        const userId = userToEdit.user?.id || userToEdit.user_id
        if (!userId) {
          console.error('User ID not found')
          return
        }

        const phoneDigits = phoneNumber.replace(/\D/g, '')
        const wantsRepairAccess = grantRepairAccess
        const wantsTransporterAccess = grantTransporterAccess
        const resolvedUserType = resolveAccessLabel(wantsRepairAccess, wantsTransporterAccess)

        await apiClient.portal.admin.editFirebaseUser({
          userId,
          email: email.toLowerCase(),
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          phone: phoneDigits,
          role: 'logistics',
          uploader_access: {
            repair: wantsRepairAccess,
            transporter: wantsTransporterAccess,
          },
          ...(wantsTransporterAccess ? { transporter_role: transporterRole } : { transporter_role: null }),
        })

          onSuccess({
          id: userId,
          email: email.toLowerCase(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          phoneNumber: phoneDigits,
          transporterRole: resolvedUserType === 'repair' ? 'repair' : transporterRole,
          userType: resolvedUserType,
          elevated: elevated,
          driverLicenseFrontStoragePath: driverLicenseFrontStoragePath || undefined,
          repairShopIds: wantsRepairAccess ? selectedRepairShopIds : undefined,
          repairShopId: wantsRepairAccess ? selectedRepairShopIds[0] : undefined,
        })
      } else {
        const phoneDigits = phoneNumber.replace(/\D/g, '')
        const resolvedUserType = resolveAccessLabel(grantRepairAccess, grantTransporterAccess)
        const baseUserData = {
          email: email.toLowerCase(),
          password: '', // Will be generated by the API
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          phone: phoneDigits,
          permissions: [],
          email_verified: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          needsOnboarding: true,
          disabled: false,
          dealership_name: '',
          is_active: true,
          bank_id: '',
          notes: `Logistics uploader: ${firstName.trim()} ${lastName.trim()}`,
        }

        const shopsForLabels =
          repairShopLocations?.filter(
            (shop): shop is typeof shop & { id: string } =>
              typeof shop.id === 'string' && selectedRepairShopIds.includes(shop.id)
          ) ?? []
        const repairShopName = shopsForLabels
          .map((s) => s.name || s.contact_name || s.id)
          .filter(Boolean)
          .join(', ')

          const response = await apiClient.portal.admin.createLogisticsUser({
            ...baseUserData,
            role: 'logistics',
            ...(grantRepairAccess ? { shop_ids: selectedRepairShopIds } : {}),
            uploader_access: {
              repair: grantRepairAccess,
              transporter: grantTransporterAccess,
            },
            transporter_role: grantTransporterAccess ? transporterRole : undefined,
            ...(driverLicenseFrontStoragePath
              ? { driver_license_front_storage_path: driverLicenseFrontStoragePath }
              : {}),
          } as any)

          let companyName = ''
          if (grantTransporterAccess && selectedCompanyId) {
            try {
              const selectedCompany = transporterGroups.find(group => group.id === selectedCompanyId)
              if (selectedCompany) {
                companyName = selectedCompany.name
                
                const currentDriverIds = selectedCompany.driver_ids || []
                const currentAdminIds = selectedCompany.admin_ids || []
                
                if (transporterRole === 'driver') {
                  await updateTransporterGroup({
                    groupId: selectedCompanyId,
                    driverIds: [...currentDriverIds, response.userId],
                    adminIds: currentAdminIds
                  })
                } else if (transporterRole === 'admin' || transporterRole === 'owner') {
                  await updateTransporterGroup({
                    groupId: selectedCompanyId,
                    driverIds: currentDriverIds,
                    adminIds: [...currentAdminIds, response.userId]
                  })
                }
              }
            } catch (companyError) {
              console.error('Error adding user to company:', companyError)
            }
          }

          onSuccess({
            id: response.userId,
            email: baseUserData.email,
            firstName: baseUserData.first_name,
            lastName: baseUserData.last_name,
            phoneNumber: baseUserData.phone,
            transporterRole:
              grantRepairAccess && !grantTransporterAccess ? 'repair' : transporterRole,
            userType: resolvedUserType,
            elevated: elevated,
            driverLicenseFrontStoragePath: driverLicenseFrontStoragePath || undefined,
            companyId: selectedCompanyId || undefined,
            companyName: companyName || undefined,
            repairShopIds: grantRepairAccess ? selectedRepairShopIds : undefined,
            repairShopId: grantRepairAccess ? selectedRepairShopIds[0] : undefined,
            repairShopName: grantRepairAccess ? repairShopName || undefined : undefined,
          })
      }
      
      handleClose()
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} logistics user:`, error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setEmail('')
    setFirstName('')
    setLastName('')
    setPhoneNumber('')
    setTransporterRole('owner')
    setGrantRepairAccess(false)
    setGrantTransporterAccess(true)
    setSelectedCompanyId('')
    setSelectedRepairShopIds([])
    setDriverLicenseFrontStoragePath('')
    setDriverLicenseFrontUrl('')
    setLicensePhotoError('')
    setErrors({
      email: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      transporterRole: '',
      access: '',
      repairShop: '',
    })
    onClose()
  }

  const handleUploadLicensePhoto = async (file?: File) => {
    if (!file) return
    try {
      setIsUploadingLicensePhoto(true)
      setLicensePhotoError('')
      const base64 = await fileToBase64(file)
      const result = await apiClient.portal.admin.uploadTransporterLicenseFront(
        base64,
        selectedCompanyId || undefined,
        userToEdit?.id
      )
      const storagePath = (result as any)?.data?.storagePath ?? (result as any)?.storagePath
      const previewUrl = (result as any)?.data?.url ?? (result as any)?.url
      if (!storagePath) {
        throw new Error('Upload did not return a storage path')
      }
      setDriverLicenseFrontStoragePath(storagePath)
      if (previewUrl) setDriverLicenseFrontUrl(previewUrl)
    } catch (error) {
      console.error('Failed uploading driver license photo:', error)
      setLicensePhotoError(error instanceof Error ? error.message : 'Failed to upload photo')
    } finally {
      setIsUploadingLicensePhoto(false)
    }
  }

  const hasUploadedLicense = !!driverLicenseFrontStoragePath
  const hasLicensePhoto = hasUploadedLicense && !!driverLicenseFrontUrl
  const isCreatingDualUser =
    !isEditMode && grantRepairAccess && grantTransporterAccess

  const repairShopOptions = useMemo(() => {
    const filtered = filterRepairShopsForPicker(repairShopLocations, {
      includeInactive: includeInactiveRepairShops,
    })
    // Keep already-selected shops visible even if inactive and filter is off
    const selected = (repairShopLocations ?? []).filter(
      (s) => typeof s.id === 'string' && selectedRepairShopIds.includes(s.id),
    )
    const byId = new Map<string, (typeof filtered)[number]>()
    for (const shop of [...filtered, ...selected]) {
      if (shop.id) byId.set(shop.id, shop)
    }
    return Array.from(byId.values())
  }, [repairShopLocations, includeInactiveRepairShops, selectedRepairShopIds])
  const selectedRepairShopOptions = useMemo(
    () =>
      repairShopOptions.filter(
        (s): s is (typeof s & { id: string }) =>
          typeof s.id === 'string' && selectedRepairShopIds.includes(s.id)
      ),
    [repairShopOptions, selectedRepairShopIds]
  )



  const contactSectionInfo = (
    <Box sx={{ maxWidth: 300 }}>
      <Typography variant="body2" component="div">
        Email cannot be changed when editing an existing user.
        Phone must be a valid 10-digit US number.
      </Typography>
    </Box>
  )

  const transporterSectionInfo = (
    <Box sx={{ maxWidth: 340 }}>
      <Typography variant="body2" component="div" gutterBottom>
        <strong>Role</strong> — Driver: view and accept assigned legs. Admin: view all shipments and assign
        legs to drivers, but cannot drive themselves. Owner: full shipment visibility and can drive themselves. Default for new users is Owner.
      </Typography>
      <Typography variant="body2" component="div" gutterBottom>
        <strong>Elevated</strong> is only shown when role is Driver; it grants owner permissions to drivers.
      </Typography>
      <Typography variant="body2" component="div" gutterBottom>
        <strong>Company</strong> (new users only): optionally add them to a transporter company in the selected
        role. They receive an email with uploader login credentials.
      </Typography>
    </Box>
  )

  const driversLicenseSectionInfo = (
    <Box sx={{ maxWidth: 300 }}>
      <Typography variant="body2" component="div">
        Upload an image of the front of the driver&apos;s license. It is stored for this transporter profile and
        can be reviewed by admins.
      </Typography>
    </Box>
  )

  const repairSectionInfo = (
    <Box sx={{ maxWidth: 340 }}>
      <Typography variant="body2" component="div">
        Shop users use existing repair / logistics access — there is no separate shop role. Pick one
        or more repair shops. In the uploader they can switch shops to see trucks at each location.
      </Typography>
    </Box>
  )

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          {isEditMode
            ? 'Edit shop user'
            : isCreatingDualUser
              ? 'Create shop user (repair & transport)'
              : grantRepairAccess
                ? 'Create shop user'
                : 'Create logistics user'}
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          <Box>
            <FormControl error={!!errors.access} component="fieldset" variant="standard">
              <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap" useFlexGap>
                <FormControlLabel
                  control={
                    <Switch
                      checked={grantTransporterAccess}
                      onChange={(e) => setGrantTransporterAccess(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Transporter"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={grantRepairAccess}
                      onChange={(e) => setGrantRepairAccess(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Repair"
                />
              </Stack>
              {errors.access && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75 }}>
                  {errors.access}
                </Typography>
              )}
            </FormControl>
          </Box>

          <Box>
            <SectionHeading title="Contact information" info={contactSectionInfo} />
            <Stack spacing={2}>
              <TextField
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!errors.email}
                helperText={errors.email || undefined}
                FormHelperTextProps={{ sx: { m: 0 } }}
                fullWidth
                required
                disabled={isEditMode}
              />
              <TextField
                label="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                error={!!errors.firstName}
                helperText={errors.firstName || undefined}
                FormHelperTextProps={{ sx: { m: 0 } }}
                fullWidth
                required
              />
              <TextField
                label="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                error={!!errors.lastName}
                helperText={errors.lastName || undefined}
                FormHelperTextProps={{ sx: { m: 0 } }}
                fullWidth
                required
              />
              <TextField
                label="Phone Number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber || undefined}
                FormHelperTextProps={{ sx: { m: 0 } }}
                fullWidth
                required
              />
            </Stack>
          </Box>

          {grantTransporterAccess && (
            <Box>
              <SectionHeading title="Transporter" info={transporterSectionInfo} />
              <Stack spacing={2.5}>
                <Stack
                  direction="row"
                  spacing={2}
                  flexWrap="wrap"
                  useFlexGap
                  alignItems="flex-start"
                >
                  <FormControl
                    sx={{ flex: '1 1 200px', minWidth: 200, maxWidth: { sm: 380 } }}
                    fullWidth
                    error={!!errors.transporterRole}
                  >
                    <InputLabel id="transporter-role-label">Transporter Role</InputLabel>
                    <Select
                      labelId="transporter-role-label"
                      id="transporter-role-select"
                      value={transporterRole}
                      label="Transporter Role"
                      onChange={(e) => {
                        const next = e.target.value as TransporterRole
                        setTransporterRole(next)
                        if (next !== 'driver') {
                          setElevated(false)
                        }
                      }}
                    >
                      <MenuItem value="driver">Driver</MenuItem>
                      <MenuItem value="admin">Admin</MenuItem>
                      <MenuItem value="owner">Owner</MenuItem>
                    </Select>
                    {errors.transporterRole && (
                      <FormHelperText>{errors.transporterRole}</FormHelperText>
                    )}
                  </FormControl>
                  <FormControlLabel
                    sx={{
                      m: 0,
                      flex: '0 0 auto',
                      alignSelf: 'center',
                      minHeight: 56,
                      pr: 0.5,
                    }}
                    control={
                      <Checkbox
                        checked={elevated}
                        disabled={transporterRole !== 'driver'}
                        onChange={(e) => setElevated(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Elevated"
                  />
                </Stack>

                {!isEditMode && (
                  <FormControl fullWidth>
                    <InputLabel id="company-select-label">Assign to Company (Optional)</InputLabel>
                    <Select
                      labelId="company-select-label"
                      id="company-select"
                      value={selectedCompanyId}
                      label="Assign to Company (Optional)"
                      onChange={(e) => setSelectedCompanyId(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>No company assignment</em>
                      </MenuItem>
                      {transporterGroups.map((group) => (
                        <MenuItem key={group.id} value={group.id}>
                          {group.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    p: 1.5,
                  }}
                >
                  <SectionHeading
                    dense
                    title={"Driver's license (front)"}
                    info={driversLicenseSectionInfo}
                  />

                  {hasLicensePhoto ? (
                    <Box position="relative" mb={1.5}>
                      <Box
                        component="img"
                        src={driverLicenseFrontUrl}
                        alt="Driver's license front"
                        sx={{
                          width: '100%',
                          maxHeight: 220,
                          objectFit: 'contain',
                          borderRadius: 1,
                          bgcolor: 'grey.100',
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          color: 'success.main',
                          bgcolor: 'background.paper',
                          borderRadius: '50%',
                          p: 0.25,
                          lineHeight: 0,
                        }}
                      >
                        <CheckCircle fontSize="small" />
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: 120,
                        borderRadius: 1,
                        bgcolor: 'grey.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 1.5,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        No photo uploaded yet
                      </Typography>
                    </Box>
                  )}

                  <Box display="flex" flexWrap="wrap" gap={1}>
                    <Button
                      size="small"
                      variant="contained"
                      component="label"
                      disabled={isUploadingLicensePhoto}
                    >
                      {isUploadingLicensePhoto ? 'Uploading...' : 'Upload photo'}
                      <input
                        hidden
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          void handleUploadLicensePhoto(file)
                          e.currentTarget.value = ''
                        }}
                      />
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!hasUploadedLicense}
                      onClick={async () => {
                        try {
                          if (driverLicenseFrontUrl) {
                            window.open(driverLicenseFrontUrl, '_blank', 'noopener,noreferrer')
                            return
                          }
                          const result = await apiClient.portal.admin.getTransporterLicenseFrontUrl(
                            driverLicenseFrontStoragePath
                          )
                          const url = (result as any)?.data?.url ?? (result as any)?.url
                          if (url) window.open(url, '_blank', 'noopener,noreferrer')
                        } catch (error) {
                          console.error('Failed opening driver license photo:', error)
                        }
                      }}
                    >
                      View photo
                    </Button>
                    {hasUploadedLicense && (
                      <Button
                        size="small"
                        variant="text"
                        color="error"
                        onClick={() => {
                          setDriverLicenseFrontStoragePath('')
                          setDriverLicenseFrontUrl('')
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </Box>
                  {licensePhotoError && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75 }}>
                      {licensePhotoError}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Box>
          )}

          {grantRepairAccess && (
            <Box>
              <SectionHeading title="Repair" info={repairSectionInfo} />
              <Autocomplete
                multiple
                fullWidth
                options={repairShopOptions}
                value={selectedRepairShopOptions}
                onChange={(_, newValues) => {
                  setSelectedRepairShopIds(
                    newValues.map((v) => v.id).filter((id): id is string => typeof id === 'string')
                  )
                }}
                getOptionLabel={(option) =>
                  `${option.name || option.contact_name || option.id || ''}${
                    option.is_active === false ? ' (inactive)' : ''
                  }`
                }
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Repair shops"
                    error={!!errors.repairShop}
                    helperText={errors.repairShop || undefined}
                    FormHelperTextProps={{ sx: { m: 0 } }}
                  />
                )}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeInactiveRepairShops}
                    onChange={(e) => setIncludeInactiveRepairShops(e.target.checked)}
                    size="small"
                  />
                }
                label="Include inactive shops"
                sx={{ mt: 0.5 }}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button onClick={handleClose} variant="outlined" color="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} color="inherit" />
              {isEditMode ? 'Updating...' : 'Creating...'}
            </Box>
          ) : (
            isEditMode ? 'Update User' : 'Create User'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CreateUserDialog 